import { useMemo } from "react"
import * as THREE from "three"
import { MeshSurfaceSampler } from "three/examples/jsm/math/MeshSurfaceSampler"

export type ModelProperties = {
  stress: number
  viscosity: number
  pressure: number
  temperature: number
  torque: number
}

export type MeshPoint = {
  position: [number, number, number]
} & ModelProperties

type UseMeshPointsOptions = {
  mesh: THREE.Mesh | null
  sampleCount?: number
}

export function useMeshPoints({
  mesh,
  sampleCount = 10000,
}: UseMeshPointsOptions): MeshPoint[] {
  const points = useMemo<MeshPoint[]>(() => {
    const geometry = mesh?.geometry
    if (!geometry) return []

    if (!geometry.boundingBox) geometry.computeBoundingBox()
    const bb = geometry.boundingBox!
    const size = new THREE.Vector3().subVectors(bb.max, bb.min)
    const ref = Math.max(size.x, size.y, size.z) || 1

   const R = {
  stress: 0.4 * ref,      // abans 0.15
  pressure: 0.30 * ref,    // abans 0.10
  temperature: 0.40 * ref, // abans 0.20
  torque: 0.25 * ref,      // abans 0.12
}

    const sampler = new MeshSurfaceSampler(new THREE.Mesh(geometry)).build()

    const sampledPos = new THREE.Vector3()
    const workPos = new THREE.Vector3()

    const stressSources   = getRandomFocalPointsOnSurface(sampler, 4)
    const tempSources     = getRandomFocalPointsOnSurface(sampler, 3)
    const pressureSources = getRandomFocalPointsOnSurface(sampler, 2)
    const torqueSources   = getRandomFocalPointsOnSurface(sampler, 5)

    const out: MeshPoint[] = []

    for (let i = 0; i < sampleCount; i++) {
      sampler.sample(sampledPos)

      const x = sampledPos.x
      const y = sampledPos.y
      const z = sampledPos.z
      workPos.set(x, y, z)

      const sMin = smoothMinDistance(workPos, stressSources,   R.stress,     0.7)
      const pMin = smoothMinDistance(workPos, pressureSources, R.pressure,   1.2)
      const tMin = smoothMinDistance(workPos, tempSources,     R.temperature,0.5)
      const qMin = smoothMinDistance(workPos, torqueSources,   R.torque,     0.9)

      const stress      = 1 - sMin
      const viscosity   = lerp(100, 130, stress)
      const pressure    = lerp(10,  25,  1 - pMin)
      const temperature = lerp(20,  50,  1 - tMin)
      const torque      = lerp(0,   6,   1 - qMin)

      out.push({ position: [x, y, z], stress, viscosity, pressure, temperature, torque })
    }

    return out
  }, [mesh?.geometry, sampleCount])

  return points
}

function getRandomFocalPointsOnSurface(sampler: MeshSurfaceSampler, count: number) {
  const pts: THREE.Vector3[] = []
  const v = new THREE.Vector3()
  for (let i = 0; i < count; i++) {
    sampler.sample(v)
    pts.push(v.clone())
  }
  return pts
}

function smoothMinDistance(
  point: THREE.Vector3,
  targets: THREE.Vector3[],
  maxDistance: number,
  curve: number
) {
  let minDist = Infinity
  for (const t of targets) {
    const d = point.distanceTo(t)
    if (d < minDist) minDist = d
  }
  const normalized = THREE.MathUtils.clamp(minDist / Math.max(1e-9, maxDistance), 0, 1)
  return Math.pow(normalized, curve)
}

function lerp(min: number, max: number, t: number) {
  return min + (max - min) * t
}
